import React, { useEffect, useRef, useState } from 'react';
import { LuRefreshCw } from 'react-icons/lu';
import Button from '../common/Button';
import { STRINGS } from '../../constants/strings';
import useGeneralStore from '../../store/generalStore';

const UpdateDialog = ({ isOpen, onClose }) => {
  const dialogRef = useRef(null);
  const [isUpdating, setIsUpdating] = useState(false);
  
  const { currentAppVersion, latestAppVersion } = useGeneralStore();

  useEffect(() => {
    const dialog = dialogRef.current;
    
    if (isOpen && dialog) {
      dialog.showModal();
    } else if (dialog) {
      dialog.close();
    }
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    // Close when clicking on the backdrop (outside the dialog content)
    if (e.target === dialogRef.current) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    // Close on Escape key
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const handleUpdate = async () => {
    setIsUpdating(true);
    
    try {
      // Clear browser caches to force fresh resource loading
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
      }
      
      // Force reload with cache bypass - this will fetch fresh resources
      // while preserving localStorage (Zustand persistent storage)
      window.location.reload(true);
      
      // Fallback: if reload doesn't work, try a more aggressive approach
      setTimeout(() => {
        window.location.href = window.location.href;
      }, 100);
      
    } catch (error) {
      console.error('Error during update:', error);
      // Fallback to simple reload
      window.location.reload();
    }
  };

  return (
    <dialog
      ref={dialogRef}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      onClose={onClose}
      aria-label={STRINGS.DIALOGS.APP_UPDATE.TITLE}
      className="m-auto w-full max-w-md rounded-xl border border-ui-line bg-ui-card p-0 text-ui-ink shadow-pop backdrop:bg-black/40"
    >
      <div className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
            <LuRefreshCw aria-hidden className="size-5" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-ui-ink">{STRINGS.DIALOGS.APP_UPDATE.TITLE}</h2>
            <p className="mt-0.5 text-sm text-ui-ink-2">{STRINGS.DIALOGS.APP_UPDATE.SUBTITLE}</p>
            <p className="mt-3 text-sm leading-relaxed text-ui-ink-2">{STRINGS.DIALOGS.APP_UPDATE.MESSAGE}</p>
          </div>
        </div>

        <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
          <div className="rounded-lg bg-ui-muted px-3 py-2">
            <dt className="text-xs text-ui-ink-3">Your version</dt>
            <dd className="mt-0.5 font-mono text-ui-ink-2">{currentAppVersion}</dd>
          </div>
          <div className="rounded-lg bg-emerald-50 px-3 py-2 dark:bg-emerald-500/10">
            <dt className="text-xs text-emerald-800 dark:text-emerald-300">New version</dt>
            <dd className="mt-0.5 font-mono text-ui-ink">{latestAppVersion}</dd>
          </div>
        </dl>

        <p className="mt-4 text-xs text-ui-ink-3">
          The dashboard reloads to get the new version. Anything you have not saved will be lost.
        </p>

        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={isUpdating}>
            {STRINGS.DIALOGS.APP_UPDATE.BUTTON_LATER}
          </Button>
          <Button onClick={handleUpdate} disabled={isUpdating} loading={isUpdating}>
            {isUpdating ? STRINGS.DIALOGS.APP_UPDATE.UPDATING : STRINGS.DIALOGS.APP_UPDATE.BUTTON_UPDATE}
          </Button>
        </div>
      </div>
    </dialog>
  );
};

export default UpdateDialog;
